import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  AttributeFollowUpType,
  AttributeType,
  ItemAttributeLinkType,
  ItemType,
  SettingsType,
  ValidSizingCalculatorAttributesType
} from 'data/types';
import {
  setStructureAttachments,
  unassignImageFromStructureItem
} from 'features/structure/structure-slice';
import { FIREPLACE_APPLIANCE_ITEM } from 'utils/appliances';
import axios from 'utils/api';
import { parseSizingBandsSetting } from 'utils/ccb-band-logic';
import { removeNullishValues } from 'utils/helpers';
import {
  fetchRecommendedSizingDefaults,
  transformSizingItemDefaultsFromApi
} from 'utils/recommended-sizing-defaults';
import {
  hydrateDefaultSizingItems,
  setAirHandlerProducts,
  setAirHandlerTypes,
  setApplianceItems,
  setAttributes,
  setBTULimits,
  setCcbCapacityBandSettings,
  setCondenserProducts,
  setCriticalDataError,
  setDefaultSizingItems,
  setDuctingComponents,
  setDuctingKitProducts,
  setFenestration,
  setInsulationLevelSettings,
  setItems,
  setLineSetCoverProducts,
  setMaxDuctlessZones,
  setModifiedDefaultCategories,
  setNonCriticalDataError,
  setOversizeRanges,
  setRestockData,
  setSizingBands,
  setStructureTypes,
  setSupplementalCoolingWeight,
  setSupplementalHeatingWeight,
  setSupplementalHeatingWeightLoaded,
  setTextKeys,
  setValidAirHandlerCodes,
  setValidItems,
  setValidSizingAttributes
} from './data-slice';

export const fetchDefaultData = createAsyncThunk(
  'data/fetchDefaultData',
  async (_, { dispatch }) => {
    try {
      const newItems = (await axios.get('items')).data as ItemType[];
      const validSizingAttributes = (await axios.get('valid-sizing-attributes'))
        .data as ValidSizingCalculatorAttributesType;
      const itemAttributeLinks = (await axios.get('item-attribute-links'))
        .data as ItemAttributeLinkType[];
      const attributes = (await axios.get('attributes'))
        .data as AttributeType[];
      const attributeFollowUps = (await axios.get('attribute-followups'))
        .data as AttributeFollowUpType[];
      const formattedAttributes = attributes.map((attr) => {
        const followUps = attributeFollowUps.filter(
          (af) => af.followUpAttributeId === attr.id
        );
        if (followUps.length === 0) {
          return removeNullishValues(attr);
        }
        return {
          ...removeNullishValues(attr),
          parentAttributes: followUps.map((f) => ({
            attributeId: f.attributeId,
            value: f.value
          }))
        };
      });
      const formattedItems: ItemType[] = newItems.map((item) => {
        const links = itemAttributeLinks.filter(
          (link) => link.itemId === item.id
        );
        const itemAttributeIds = links.map((link) => link.attributeId);
        const itemAttributes = formattedAttributes.filter((attr) =>
          itemAttributeIds.includes(attr.id)
        );
        const formattedItemAttributes: AttributeType[] = itemAttributes.map(
          (attr) => {
            const link = links.find((l) => l.attributeId === attr.id);
            return {
              ...attr,
              value: link ? link.defaultValue : undefined,
              required: !!link?.required
            };
          }
        );

        attributeFollowUps.forEach((followUp) => {
          if (
            itemAttributeIds.includes(followUp.attributeId) &&
            !itemAttributeIds.includes(followUp.followUpAttributeId)
          ) {
            const newAttribute = formattedAttributes.find(
              (attr) => attr.id === followUp.followUpAttributeId
            );
            if (newAttribute) {
              formattedItemAttributes.push({
                ...newAttribute,
                value: undefined
              });
            }
          }
        });

        return {
          ...item,
          attributes: formattedItemAttributes
        };
      });

      dispatch(setItems(formattedItems));
      dispatch(setAttributes(formattedAttributes));
      dispatch(setValidSizingAttributes(validSizingAttributes));
    } catch (error) {
      console.error('Error fetching critical data', error);
      dispatch(setCriticalDataError(true));
    }

    await Promise.all([
      axios
        .get('air-handlers')
        .then((res) => dispatch(setAirHandlerProducts(res.data))),
      axios
        .get('condensers')
        .then((res) => dispatch(setCondenserProducts(res.data))),
      axios
        .get('structure-types')
        .then((res) => dispatch(setStructureTypes(res.data))),
      axios
        .get('fenestration')
        .then((res) => dispatch(setFenestration(res.data))),
      axios.get('valid-items').then((res) => dispatch(setValidItems(res.data))),
      axios
        .get('ducting-kits')
        .then((res) => dispatch(setDuctingKitProducts(res.data))),
      axios
        .get('oversize-ranges')
        .then((res) => dispatch(setOversizeRanges(res.data))),
      // Like oversize-ranges, the supplemental heating weight shapes
      // valid-systems request payloads; a failed load must block rather
      // than silently diverge from the server's supplemental oversize logic.
      axios.get('settings').then((res) => {
        const settings = res.data as SettingsType;
        const {
          heatingSupplementalLoadPercent,
          coolingSupplementalLoadPercent
        } = settings;
        // Same validation as the server's parseSupplementalHeatingWeight;
        // invalid values keep the 0.5 default.
        if (
          Number.isFinite(heatingSupplementalLoadPercent) &&
          heatingSupplementalLoadPercent > 0
        ) {
          dispatch(
            setSupplementalHeatingWeight(heatingSupplementalLoadPercent / 100)
          );
        }
        if (
          typeof coolingSupplementalLoadPercent === 'number' &&
          Number.isFinite(coolingSupplementalLoadPercent) &&
          coolingSupplementalLoadPercent > 0
        ) {
          dispatch(
            setSupplementalCoolingWeight(coolingSupplementalLoadPercent / 100)
          );
        }
        // Flag means "settings response processed" — an invalid/missing
        // value legitimately keeps the 0.5 default (= server fallback).
        dispatch(setSupplementalHeatingWeightLoaded(true));
        // CCB capacity band multipliers (AHA-6681). Invalid or missing
        // values keep the slice defaults (1.0 / 1.5 — the migration seed).
        const ccbLowerBound = settings['ccb-capacity-band-lower-bound'];
        const ccbUpperMultiplier =
          settings['ccb-capacity-band-upper-multiplier'];
        if (
          typeof ccbLowerBound === 'number' &&
          typeof ccbUpperMultiplier === 'number' &&
          Number.isFinite(ccbLowerBound) &&
          Number.isFinite(ccbUpperMultiplier) &&
          ccbLowerBound >= 0 &&
          ccbUpperMultiplier > ccbLowerBound
        ) {
          dispatch(
            setCcbCapacityBandSettings({
              lowerBound: ccbLowerBound,
              upperMultiplier: ccbUpperMultiplier
            })
          );
        }
        // Per-equipment-type sizing bands (AHA-6796). Invalid or missing
        // leaves the slice null → the legacy multipliers above stay the
        // live fallback.
        const sizingBands = parseSizingBandsSetting(settings.sizingBands);
        if (sizingBands) {
          dispatch(setSizingBands(sizingBands));
        }
      }),
      axios
        .get('air-handler-types')
        .then((res) => dispatch(setAirHandlerTypes(res.data))),
      axios
        .get('appliances')
        .then((res) =>
          dispatch(setApplianceItems([...res.data, FIREPLACE_APPLIANCE_ITEM]))
        ),
      axios
        .get('sizing/insulation-level-settings')
        .then((res) => dispatch(setInsulationLevelSettings(res.data)))
    ]).catch((error) => {
      console.error('Error fetching critical data', error);
      dispatch(setCriticalDataError(true));
    });

    await Promise.all([
      axios
        .get('line-set-covers')
        .then((res) => dispatch(setLineSetCoverProducts(res.data))),
      axios
        .get('ducting-components')
        .then((res) => dispatch(setDuctingComponents(res.data))),
      axios
        .get('restock-data')
        .then((res) => dispatch(setRestockData(res.data))),
      axios.get('btu-limits').then((res) => {
        const newBTULimits = res.data as number[][];
        const newMaxDuctlessZones =
          newBTULimits.toSorted((a, b) => b.length - a.length)[0]?.length ?? 5;
        dispatch(setBTULimits(newBTULimits));
        dispatch(setMaxDuctlessZones(newMaxDuctlessZones));
      }),
      axios
        .get('products/air-handler-sizes-and-types')
        .then((res) => dispatch(setValidAirHandlerCodes(res.data))),
      axios.get('text-keys').then((res) => dispatch(setTextKeys(res.data)))
    ]).catch((error) => {
      console.error('Error fetching non-critical data', error);
      dispatch(setNonCriticalDataError(true));
    });
  }
);

export const fetchSizingItemDefaults = createAsyncThunk(
  'data/fetchSizingItemDefaults',
  async (structureUniqueId: string | undefined, { dispatch, getState }) => {
    try {
      const versionAtRequest = (getState() as RootState).data
        .defaultSizingItemsVersion;
      const params = structureUniqueId ? { structureUniqueId } : {};
      const response = await axios.get('structure/sizing-item-defaults', {
        params
      });
      const transformedDefaults = transformSizingItemDefaultsFromApi(
        response.data as Record<string, Record<string, unknown>>
      );

      // Stale-response guard: any bump of defaultSizingItemsVersion while
      // the GET was in flight is an authoritative local write already being
      // persisted by the autosave watcher — hydrating this stale snapshot
      // over it is always wrong.
      const versionNow = (getState() as RootState).data
        .defaultSizingItemsVersion;
      if (versionNow !== versionAtRequest) {
        return;
      }

      dispatch(hydrateDefaultSizingItems(transformedDefaults));
    } catch (error) {
      console.error('Error fetching sizing item defaults:', error);
    }
  }
);

export const resetFineTuningDefaults = createAsyncThunk(
  'data/resetFineTuningDefaults',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const yearBuilt =
      state.project.projectSettings.find(
        (setting) => setting.code === 'year_built'
      )?.value || state.project.attomData?.summary?.yearBuilt;
    const climateZone = state.project.locationData?.climateZone || '4';

    const recommended = await fetchRecommendedSizingDefaults(
      state.data.defaultSizingItems,
      { yearBuilt, climateZone }
    );

    dispatch(setDefaultSizingItems(recommended));
    dispatch(
      setModifiedDefaultCategories({
        fenestration: false,
        walls: false,
        ceilings: false,
        floors: false
      })
    );
  }
);

export const deleteStructureImage = createAsyncThunk(
  'data/deleteStructureImage',
  async (imageId: number, { dispatch, getState }) => {
    try {
      await axios.delete(`structure/structure-images?imageId=${imageId}`);
      const state = getState() as RootState;
      const { structureAttachments } = state.structure;
      dispatch(
        setStructureAttachments(
          structureAttachments.filter((a) => a.id !== imageId)
        )
      );
      dispatch(unassignImageFromStructureItem({ attachmentId: imageId }));
    } catch (error) {
      console.error('Error deleting structure image:', error);
    }
  }
);
